import { find } from 'lodash';
import { useCallback } from 'react';

import { isFacility } from '../../utils/smartChoice.utils';
import { useLagoon } from '../useLagoon';
import {
  LagoonData,
  LagoonMessage,
  LagoonMessageContext,
  MessageKey,
  MessageResolver,
} from './useLagoonMessage.types';

/**
 * Resolves the appropriate suffix based on provider context
 * @param chipValue - Current chip category value
 * @param providerType - Provider type (ORGANIZATION, PROVIDER, etc.)
 * @returns Suffix string (e.g., '_FACILITY', '_DENTAL') or empty string
 */
export function resolveSuffix(chipValue: string, providerType: string): string {
  // If chipValue or providerType are not provided, return empty suffix
  if (!chipValue || !providerType) {
    return '';
  }

  if (isFacility(chipValue, providerType)) {
    return '_FACILITY';
  }

  // Future extensions:
  // if (isDental(chipValue, providerType)) return '_DENTAL';
  // if (isVision(chipValue, providerType)) return '_VISION';
  // if (isBehavioralHealth(chipValue, providerType)) return '_BH';

  return '';
}

/**
 * Resolves a single message from Lagoon data
 * @param baseKey - Base message key (e.g., 'CONVENIENCE_BREAKDOWN_DESCRIPTION', 'FEATURE_INCLUSION')
 * @param suffix - Suffix to apply (e.g., '_FACILITY')
 * @param lagoonData - Array of Lagoon messages
 * @returns Resolved message or undefined if not found
 */
export function resolveMessage(
  baseKey: string,
  suffix: string,
  lagoonData: LagoonData
): LagoonMessage | undefined {
  // When passing constants directly (e.g., ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN.CONVENIENCE_BREAKDOWN_DESCRIPTION),
  // the baseKey is already the string value we need to look up in lagoonData
  // We just need to apply the suffix and find the matching message
  const finalKey = suffix ? `${baseKey}${suffix}` : baseKey;

  return find(lagoonData, { key: finalKey });
}

/**
 * Custom hook for resolving Lagoon messages with automatic suffix detection
 * @param context - Context containing chipValue, providerType, and optional lagoonData
 * @returns Message resolver function that accepts single key or array of keys
 * @example
 * // Single message
 * const getMessage = useLagoonMessage({ chipValue, providerType });
 * const description = getMessage('CONVENIENCE_BREAKDOWN_DESCRIPTION');
 *
 * // Multiple messages
 * const [title, description] = getMessage([
 *   'CONVENIENCE_BREAKDOWN_TITLE',
 *   'CONVENIENCE_BREAKDOWN_DESCRIPTION'
 * ]);
 */
export function useLagoonMessage(
  context: LagoonMessageContext
): MessageResolver {
  const {
    chipValue = '',
    providerType = '',
    lagoonData: providedLagoonData,
  } = context;

  // Fetch lagoon data if not provided
  const fetchedLagoonData = useLagoon('ui-messaging')();
  const lagoonData = providedLagoonData || fetchedLagoonData || [];

  // Resolve suffix based on context (will return empty string if chipValue or providerType are not provided)
  const suffix = resolveSuffix(chipValue, providerType);

  // Create resolver function
  const resolver = useCallback(
    (input: MessageKey | MessageKey[]) => {
      // Handle array input
      if (Array.isArray(input)) {
        return input.map((key) => resolveMessage(key, suffix, lagoonData));
      }

      // Handle single key input
      return resolveMessage(input, suffix, lagoonData);
    },
    [suffix, lagoonData]
  ) as MessageResolver;

  return resolver;
}
